import { useEffect, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { AudioProgressBar } from '@/components/audio/shared/AudioProgressBar';
import { AudioBigControls } from '@/components/audio/shared/AudioBigControls';
import { AudioMiniControls } from '@/components/audio/shared/AudioMiniControls';
import { SchlafTimerSheet } from '@/components/audio/nowplaying/SchlafTimerSheet';
import { useAudioStore } from '@/stores/audioStore';

/**
 * Gesamt-Hörbuch player (B5 / C6 / flowtest-2 #5).
 *
 * TRUE 1:1 reuse of the single-story player: it renders the EXACT same
 * controls — `AudioProgressBar` (native `<input type="range">`),
 * `AudioBigControls` (±15 / play-pause), `AudioMiniControls` (speed +
 * Schlaf-Timer + download) and `SchlafTimerSheet` — all of which read and
 * drive the shared `useAudioStore` directly. There is no reimplemented scrub
 * bar and no custom drag handling: the browser owns mouse + touch + pointer
 * for the native range input, so scrubbing works under Chrome device-emulation
 * and on a real iPhone, identically to the single-story player.
 *
 * The combined audiobook is just a different track-kind (`'series-combined'`)
 * on the same engine. This component is the DISPATCHER: on mount it pre-loads
 * THIS season's combined track onto the shared singleton `<audio>` (mirroring
 * StoryInlineAudioPlayer's load-on-mount), which is what makes the shared,
 * global-store-reading controls live immediately — scrub + ±15 work before the
 * first play, not only after it. The `seriesId` guard keeps it from re-seating
 * the element on a remount / signed-url refresh and ensures we only close OUR
 * track on unmount. Every per-story side-effect (listening events, unload
 * beacon, MediaSession) is gated OFF for `'series-combined'` inside the store.
 *
 * Fed by the signed combinedAudioUrl (streaming) + combinedAudioDownloadUrl
 * (Content-Disposition:attachment → direct download) + combinedAudioDurationMs.
 */

interface Props {
  url: string;
  downloadUrl: string | null;
  durationMs: number;
  /** Visible header label (the "Gesamt-Hörbuch" heading). */
  title: string;
  /** Season title carried into the store track (loadCombined). */
  seriesTitle: string;
  seriesId: string;
  episodeCount: number;
}

export function CombinedAudioPlayer({
  url,
  downloadUrl,
  durationMs,
  title,
  seriesTitle,
  seriesId,
  episodeCount,
}: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const SC = STRINGS.series.combined;

  // Pre-load THIS season's combined track onto the shared singleton <audio>
  // the moment the player mounts — mirrors StoryInlineAudioPlayer's
  // load-on-mount. This is what makes the reused AudioProgressBar /
  // AudioBigControls / AudioMiniControls (which read the global store
  // directly) live straight away: scrub + ±15 work before the first play,
  // exactly like the single-story player. Guarded by seriesId so a remount or
  // signed-url refresh doesn't needlessly re-seat the element.
  //
  // No close-on-unmount: the combined track now SURVIVES leaving the season
  // view and is controlled off-route by the MiniPlayer (which surfaces it once
  // AppShell's onSeasonPage suppression no longer applies). On return, the
  // `alreadyThis` guard skips re-seating, so the still-playing track is simply
  // reflected — no restart, no double player.
  useEffect(() => {
    const s = useAudioStore.getState();
    const alreadyThis = s.kind === 'series-combined' && s.seriesId === seriesId;
    if (!alreadyThis) {
      s.loadCombined({ url, downloadUrl, title: seriesTitle, seriesId, durationMs });
    }
  }, [url, downloadUrl, seriesTitle, seriesId, durationMs]);

  return (
    <>
      <div className="flex flex-col items-center gap-2.5 overflow-hidden rounded-lg bg-gradient-to-t from-dream-navy to-world-aa px-8 pt-8 pb-8">
        {/* Title + "Alle X Folgen am Stück" */}
        <div className="flex w-full flex-col items-center gap-2 py-2">
          <p className="text-center text-base font-medium leading-none text-dream-white md:text-xl">
            {title}
          </p>
          <span className="text-xxs leading-3 text-dream-white opacity-50 md:text-sm">
            {SC.allEpisodes.replace('{count}', String(episodeCount))}
          </span>
        </div>

        {/* Exact single-story controls — native range + native buttons,
            dispatching to the shared store (which holds this combined track). */}
        <AudioProgressBar className="md:max-w-[468px]" />
        <AudioBigControls />
        <AudioMiniControls onSchlafTimerClick={() => setSheetOpen(true)} />
      </div>

      <SchlafTimerSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}
